import { range, uniq } from 'lodash'
import { SyntheticEvent, useState } from 'react'
import styled from 'styled-components'
import { ICapture, ISegment } from '../../shared/types'
import Cell from './cell'
import { MergeOverlay } from './elements'
import MasonryGrid from './masonry'

const GRID_WIDTH = 1040
const COLUMNS = 4
const GUTTER_WIDTH = 15
const COLUMN_WIDTH = (GRID_WIDTH - GUTTER_WIDTH * (COLUMNS - 1)) / COLUMNS

const Grid = styled.div`
    position: relative;
    width: ${GRID_WIDTH}px;
`

type Props = {
    capture: ICapture
    segments: ISegment[]
    selectedIndexes: number[]
    isSoundOn?: boolean
    isEditModalOpen?: boolean
    onEditClick: (segmentIndex: number) => void
    onDuplicateClick: (segmentIndex: number) => void
    onDeleteClick: (segmentIndex: number) => void
    onRevertClick: (segmentIndex: number) => void
    onSelect: (selectedIndexes: number[]) => void
    onMergeLeft: (segmentIndex: number) => void
    onMergeRight: (segmentIndex: number) => void
}

const SuperCutGrid = ({
    capture,
    segments,
    selectedIndexes,
    isSoundOn = false,
    isEditModalOpen = false,
    onEditClick,
    onDuplicateClick,
    onDeleteClick,
    onRevertClick,
    onSelect,
    onMergeLeft,
    onMergeRight,
}: Props) => {
    const [mergeIndex, setMergeIndex] = useState<number>(0)
    const [mergeDirection, setMergeDirection] = useState<'left' | 'right' | undefined>()

    const filteredSegments = segments
        .filter((s) => !s.editParams?.displayHidden)
        .sort((a: ISegment, b: ISegment) => {
            return (a.editParams?.displayOrder || a.index) - (b.editParams?.displayOrder || b.index)
        })

    const itemHeights = filteredSegments.map(({ editParams }) => {
        if (editParams && editParams.crop) {
            return COLUMN_WIDTH / (editParams.crop.width / editParams.crop.height)
        }

        const largestwidth = Math.max(...filteredSegments.map((o) => o.editParams?.crop?.width || capture.width))
        const largestHeight = Math.max(...filteredSegments.map((o) => o.editParams?.crop?.height || capture.height))

        return COLUMN_WIDTH / (largestwidth / largestHeight)
    })

    const onMergeHover = (index: number, direction?: 'left' | 'right') => {
        setMergeIndex(index)
        setMergeDirection(direction)
    }

    const onSelectSegment = (e: SyntheticEvent<HTMLDivElement, MouseEvent>, index: number) => {
        const foundIndex = selectedIndexes.indexOf(index)
        let newlySelected = [...selectedIndexes]

        if (e.nativeEvent.shiftKey) {
            const startIndex = selectedIndexes[selectedIndexes.length - 1] || 0
            newlySelected = uniq([
                ...selectedIndexes,
                ...range(Math.min(startIndex, index), Math.max(startIndex, index) + 1),
            ])
        } else if (foundIndex > -1) {
            newlySelected.splice(foundIndex, 1)
        } else {
            newlySelected.push(index)
        }

        onSelect(newlySelected)
    }

    return (
        <Grid>
            <MasonryGrid
                {...{
                    columns: 4,
                    gutter: GUTTER_WIDTH,
                    itemWidth: COLUMN_WIDTH,
                    itemHeights,
                }}
            >
                {filteredSegments.map((segment, i) => {
                    const isFirst = i === 0
                    const isLast = i === filteredSegments.length - 1
                    return (
                        <div key={`supercut-grid-cell-${segment.index}`}>
                            <Cell
                                {...{
                                    capture,
                                    segment,
                                    isSoundOn,
                                    width: COLUMN_WIDTH,
                                    height: itemHeights[i],
                                    isPaused: isEditModalOpen,
                                    isSelected: selectedIndexes.indexOf(segment.index) > -1,
                                    isFirst: i === 0,
                                    isLast: i === filteredSegments.length - 1,
                                    onEdit: () => onEditClick(segment.index),
                                    onSelect: (e) => onSelectSegment(e, segment.index),
                                    onDuplicate: () => onDuplicateClick(segment.index),
                                    onDelete: () => onDeleteClick(segment.index),
                                    onRevert: () => onRevertClick(segment.index),
                                    onMergeHover: (direction) => onMergeHover(i, direction),
                                    onMergeLeft: !isFirst ? () => onMergeLeft(segment.index) : undefined,
                                    onMergeRight: !isLast ? () => onMergeRight(segment.index) : undefined,
                                }}
                            >
                                {mergeDirection && i + (mergeDirection === 'left' ? 1 : -1) === mergeIndex && (
                                    <MergeOverlay>
                                        <i className={`ss-${mergeDirection}`} />
                                    </MergeOverlay>
                                )}
                            </Cell>
                        </div>
                    )
                })}
            </MasonryGrid>
        </Grid>
    )
}

export default SuperCutGrid
